import { http, unwrap, unwrapList, type ActionResponse } from './http';
import type {
  Category,
  CategoryListQuery,
  CategoryListResponse,
  CategoryScope,
  CategoryTreeNode,
  CreateCategoryInput,
  UpdateCategoryInput,
} from '@features/categories/types';

/**
 * Backend taxonomy/categories DTOs (see /api/v1/taxonomy/categories).
 *
 * Field-name differences vs. FE Category type:
 *   BE.displayOrder ↔ FE.order
 *   BE has no `scope` (taxonomy is flat across the platform).
 *   BE adds parentName, iconUrl, coverImageUrl, color, metaTitle, metaDescription,
 *   isFeatured, status (1 = Active, others = soft-deleted / hidden).
 */
interface CategoryBeDto {
  id: string;
  name: string | null;
  slug: string | null;
  description: string | null;
  parentId: string | null;
  parentName: string | null;
  displayOrder: number;
  iconUrl: string | null;
  coverImageUrl: string | null;
  color: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  isFeatured: boolean;
  status: number;
  createdAt: string;
  updatedAt: string;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
}

interface CategoryTreeBeDto {
  id: string;
  name: string | null;
  slug: string | null;
  parentId: string | null;
  displayOrder: number;
  iconUrl: string | null;
  isFeatured: boolean;
  status: number;
  children: CategoryTreeBeDto[] | null;
}

interface CreateCategoryBeRequest {
  name: string;
  slug?: string | null;
  description?: string | null;
  parentId?: string | null;
  displayOrder?: number;
  iconUrl?: string | null;
  coverImageUrl?: string | null;
  color?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  isFeatured?: boolean;
  languageId?: string | null;
  groupId?: string | null;
}

type UpdateCategoryBeRequest = CreateCategoryBeRequest;

const BASE = '/api/v1/taxonomy/categories';

/**
 * BE taxonomy is flat (no scope). FE pages still pass `scope` (ARTICLE / SSS)
 * to keep article/glossary/sss features working. We default to ARTICLE on the way back so
 * scope filtering still resolves; until BE adds scope, all categories are visible everywhere.
 */
function mapCategory(b: CategoryBeDto, scope: CategoryScope = 'ARTICLE'): Category {
  return {
    id: b.id,
    scope,
    name: b.name ?? '',
    slug: b.slug ?? '',
    parentId: b.parentId,
    description: b.description ?? undefined,
    order: b.displayOrder,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
    languageId: b.languageId ?? '',
    groupId: b.groupId ?? '',
    variantLanguageCodes: b.variantLanguageCodes ?? [],
  };
}

function mapTree(node: CategoryTreeBeDto, scope: CategoryScope = 'ARTICLE'): CategoryTreeNode {
  const children = (node.children ?? []).map((c) => mapTree(c, scope));
  return {
    id: node.id,
    scope,
    name: node.name ?? '',
    slug: node.slug ?? '',
    parentId: node.parentId,
    description: undefined,
    order: node.displayOrder,
    createdAt: '',
    updatedAt: '',
    // Tree DTO i18n alanlarını taşımaz; liste/düzenleme akışı düz `list()` kullanır.
    languageId: '',
    groupId: '',
    variantLanguageCodes: [],
    children,
  };
}

export const categoriesApi = {
  /**
   * List categories. BE returns paginated results, but FE consumers expect the legacy
   * `{ items, total }` shape. We pull pageSize=200 by default to keep tree-builders happy.
   * `scope` is accepted for FE compatibility but currently ignored by BE.
   */
  async list(query: CategoryListQuery = {}): Promise<CategoryListResponse> {
    const params: Record<string, string | number | boolean> = {
      page: 1,
      pageSize: 200,
    };
    if (query.q) params.search = query.q;
    if (query.languageId) params.languageId = query.languageId;
    const res = await http.get<ActionResponse<CategoryBeDto>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    const scope: CategoryScope = query.scope ?? 'ARTICLE';
    return {
      items: items.map((it) => mapCategory(it, scope)),
      total: pagination?.totalCount ?? items.length,
    };
  },

  /** Get the public tree (anonymous endpoint). */
  async tree(scope: CategoryScope = 'ARTICLE'): Promise<CategoryTreeNode[]> {
    const res = await http.get<ActionResponse<CategoryTreeBeDto>>(`${BASE}/tree`);
    const { items } = unwrapList(res);
    return items.map((n) => mapTree(n, scope));
  },

  async get(id: string): Promise<Category> {
    const dto = unwrap(await http.get<ActionResponse<CategoryBeDto>>(`${BASE}/${id}`));
    return mapCategory(dto);
  },

  async create(input: CreateCategoryInput): Promise<Category> {
    const body: CreateCategoryBeRequest = {
      name: input.name,
      slug: input.slug,
      description: input.description ?? null,
      parentId: input.parentId,
      displayOrder: input.order ?? 0,
      // İçerik i18n — yalnız değer varsa gönder (varyant/dil seçimi).
      ...(input.languageId ? { languageId: input.languageId } : {}),
      ...(input.groupId ? { groupId: input.groupId } : {}),
    };
    // BE returns only the new GUID in `data`; fetch the full DTO for the UI.
    const id = unwrap(await http.post<ActionResponse<string>>(BASE, body));
    const dto = unwrap(await http.get<ActionResponse<CategoryBeDto>>(`${BASE}/${id}`));
    return mapCategory(dto, input.scope);
  },

  async update(id: string, input: UpdateCategoryInput): Promise<Category> {
    // BE PUT requires the full request and returns `data: true`; merge with current
    // values to keep partial updates safe.
    const current = await this.get(id);
    const body: UpdateCategoryBeRequest = {
      name: input.name ?? current.name,
      slug: input.slug ?? current.slug,
      description: input.description ?? current.description ?? null,
      parentId: input.parentId !== undefined ? input.parentId : current.parentId,
      displayOrder: input.order ?? current.order,
    };
    await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body);
    const dto = unwrap(await http.get<ActionResponse<CategoryBeDto>>(`${BASE}/${id}`));
    return mapCategory(dto, current.scope);
  },

  async remove(id: string): Promise<void> {
    await http.delete<ActionResponse<unknown>>(`${BASE}/${id}`);
  },

  async bulkDelete(ids: string[]): Promise<{ deleted: number; skipped: number; skippedIds: string[] }> {
    const dto = unwrap(
      await http.post<
        ActionResponse<{ deleted: number; skipped: number; skippedIds: string[] | null }>
      >(`${BASE}/bulk-delete`, { ids }),
    );
    return { deleted: dto.deleted, skipped: dto.skipped, skippedIds: dto.skippedIds ?? [] };
  },
};
